/*
 * TypPage - this page is used to handle the Thank you page
*/
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getLabelFallback, convertNumber, getTotalTax, getDateInAmericanFormat, getLoggedUserMail, handleHTTPError, getMMSubtotal } from '@utils/utils';
import * as actions from "@redux-actions/";
import { useNavigate, useLocation, useSearchParams} from 'react-router-dom';
import { getOrder, getBossStoreSelected } from '@utils/services';

import PageTitle from '@components/PageTitle';
import CartProductResume from '@components/CartProductResume';
import CartLoader from '@components/CartLoader';

import useFormattedOrderItems from '@hooks/useFormattedOrderItems';
import SimpleElementSkeleton from '@components/skeletons/SimpleElementSkeleton';
import SessionModal from '@components/SessionModal';
import GenericErrorModal from '@components/GenericErrorModal';

import Visa from '@images/svg-icons/visa.svg';
import Mastercard from '@images/svg-icons/mastercard.svg';
import Discover from '@images/svg-icons/discover.svg';
import Amex from '@images/svg-icons/amex.svg';
import PayPal2 from '@images/svg-icons/paypal2.svg';
import Affirm from '@images/svg-icons/affirm.svg';
import KlarnaImg from '@images/svg-icons/klarna.svg';
import ApplePay from '@images/svg-icons/applepay.svg'

import useTealiumData from '@hooks/useTealiumData';
import useMonetate from '@hooks/useMonetate';
import useWunderkind from '@hooks/useWunderkind';
import useScrollTop from '@hooks/useScrollTop';
import md5 from 'crypto-js/md5';
import TrustPilotWidget from './TrustPilotWidget';

const TypPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    //get query string from the url
    const { search } = useLocation();
    let [searchParams, setSearchParams] = useSearchParams();
    const [imagepushed, setimagepushed] = useState(false);
    const [espotCalled, setespotCalled] = useState(false);

    //get variables from redux store
    const config = useSelector(state => state.cart.config);
    const formattedOrderItems = useSelector(state => state.cart.formattedOrderItems);
    const hostname = useSelector(state => state.cart.config.hostname);
    const orderIdFromState = useSelector(state => state.cart.orderId);
    const emailFromState = useSelector(state => state.cart.email);
    const isLoading = useSelector(state => state.cart.isLoading);
    const showSessionModal = useSelector(state => state.cart.showSessionModal);
    const showGenericErrorModal = useSelector(state => state.cart.showGenericErrorModal);
    const espotTyp = useSelector(state => state.cart.espotTyp);
    const isBossSelected = useSelector(state => state.cart.isBossSelected);
    const cart = useSelector(state => state.cart.cart);
        
    //define sizes for images
    const sizes = {
        mobile: {
            width: "145px",
            height: "94px"
        },
        desktop: {
            width: "304px",
            height: "174px"
        }
    }

    //onSubmitPrescription - this function updates the order when prescription is sended
    const onSubmitPrescription = () => {
        const orderId = !!config && !!config.orderId && config.orderId.length > 0 ?  config.orderId : orderIdFromState;
        const email = !! config && !!config.email && config.email.length > 0 ? config.email : emailFromState;

        getOrder(config.mockRequests, config.hostname, orderId, email)
            .then((res: any) => {
                setOrder(res.data);
                dispatch(actions.endLoading());

            })
            .catch(err => {
                console.error('>>> On Submit prescription get order:', err.status, ', error: ', err);
                handleHTTPError(dispatch, actions, err);
                dispatch(actions.errorLoading())
            })
    }

    //getCartProduct - for each item in the cart, create CartProductResume tile
    const getCartProduct = (orderItem) => {
        return orderItem.map((item, index) => {
            return <CartProductResume 
                key={index} 
                item={item} 
                sizes={sizes}
                prescriptionPosition='top'
                showBadge={true}
                onSubmitPrescription={() => onSubmitPrescription()}
                isTyp={true}
                />
        })
    }

    //getPaymentImage - get the image of the payment method used by user
    const getPaymentImage = (payment: string) => {
        switch(payment) {
            case 'visa': return <Visa />;
            case 'mastercard': return <Mastercard />;
            case 'amex': return <Amex />;
            case 'discover': return <Discover />;
            case 'paypal': return <PayPal2 />;
            case 'affirm': return <Affirm />;
            case 'glklarna': return <KlarnaImg />;
            case 'glapplepay' : return <ApplePay />;
        }
    }

    const login = config.callFromComponentToGl.onLogin;
    const createAccount = config.callFromComponentToGl.onCreateAccount;

    //set state variables for page
    const [order, setOrder] = useState(null);
    const [buyerName, setBuyerName] = useState(null);
    const [storeBossSelected, setStoreBossSelected] = useState(null);

    /*
        This hook is called when config changes, it looks for the query string,
        and saves user email and orderId
    */
    useEffect(() => {
        const previousQueryString = window.location.search.split('?')[1];
        const splittedQueryString = !!previousQueryString ? previousQueryString.split('&') : [];
        
        let queryString = {};
        if(splittedQueryString.length > 0) {
            splittedQueryString.forEach(s => {
                queryString[s.split('=')[0]] = s.split('=')[1];
            })
        }

        let orderId = orderIdFromState;
        let email = emailFromState;

        if(config) {
            orderId = config.orderId && config.orderId.length > 0 ? config.orderId : orderId;
            email = config.email && config.email.length > 0 ? config.email : email;
        }
        
        queryString = {...queryString, orderId: orderId, email: email};
        setSearchParams(queryString);

    }, [config]);

    /*
        This hook is called when config changes. 
        - hides custom footer
        - calls the order services, to get order object
        - attaches to tye document login and create account event
    */
    useEffect(() => {
        
        const orderId = !!config.orderId && config.orderId.length > 0 ? config.orderId : !!orderIdFromState ? orderIdFromState :  null;
        const email = !!config.email && config.email.length > 0 ? config.email : !!emailFromState ? emailFromState :  null;
        if(!!orderId && !!email) {
            config.callFromComponentToGl.hideHeaderFooter(false); 

            getOrder(config.mockRequests, config.hostname, orderId, email)
                .then((res: any) => {
                    setOrder(res.data);
                    dispatch(actions.getCartSuccess(res.data));
                    dispatch(actions.endLoading());
                })
                .catch(err => {
                    console.error('>>> Typ get order => error status:', err.status, ', error: ', err);
                    handleHTTPError(dispatch, actions, err);
                    dispatch(actions.errorLoading())
                })
                   
            !!document.getElementById('login-typ') &&
                document.getElementById('login-typ').addEventListener('click', () => {
                    login();
                });
    
            !!document.getElementById('create-account-typ') && 
                document.getElementById('create-account-typ').addEventListener('click', () => {
                    createAccount();
                });
        } else {
            //resetto il cart per tornare alla pagina di cart
            dispatch(actions.errorLoading());
            dispatch(actions.startLoading());
            navigate(config.pagesUrl.cart + search);
        }
        
    }, [config]);

    useEffect(() => {
        if (!!order && !!order.orderItem[0] && order.orderItem[0].xitem_storeIdentifier) {
          getBossStoreSelected(
            config.mockRequests,
            config.hostname,
            config.storeId,
            order.orderItem[0].xitem_storeIdentifier
          )
            .then((res: any) => {
              if (res) {
                setStoreBossSelected(res.PhysicalStore);
                dispatch(actions.setIsBossSelected(true));
              }
            })
            .catch(err => {
              console.error(
                '>>> Error on get Store information for BOSS => error status:',
                err.status,
                ', error: ',
                err
              );
              handleHTTPError(dispatch, actions, err);
            });
        }
      }, [order]);

    //hook for retrieve data to show
    useFormattedOrderItems(order);

    //This hook is called when order changes, and get the buyer name
    useEffect(() => {
        if(!!order) {
            setBuyerName(getLoggedUserMail(order.buyerDistinguishedName));
        }
    }, [order]);
    
    //This hook calls the typ espot if there are transitions lenses
    useEffect(() => {
    if(!!formattedOrderItems ) {
        const numberOfTrans= formattedOrderItems.filter(item => !!item.currentPackage.transition && item.currentPackage.transition!=null).length;
        if (!!numberOfTrans && numberOfTrans > 0 && espotCalled==false){
            dispatch(actions.getEspotTyp(config.hostname));
            setespotCalled(true);
        }
        console.log('transitions typ',numberOfTrans);
      }
    }, [formattedOrderItems]);

    //hook for set analytics data
    useTealiumData('Thankyou', order);
    
    //hook for set analytics data
    useMonetate('Thankyou', order);
 
    //hook for set Wunderkind data
    useWunderkind('Thankyou', order);

    //hook to scroll top the page
    useScrollTop();

    useEffect(() => {
        if (!order || !config?.mentionMeEnabled) return;

        console.log('Order mention me:', order);

        const firstname = order.orderItem?.[0]?.firstName || '';
        const surname = order.orderItem?.[0]?.lastName || '';
        const email = order.orderItem?.[0]?.email1 || '';
        const orderNumber = order.orderId || '';
        const orderSubtotal = getMMSubtotal(order)  || '';
        const orderCurrency = order.grandTotalCurrency || '';
        const situation = 'postpurchase';
        const locale = 'en_US';
        const couponCode = order.promotionCode?.[0]?.code || '';

        const params: string[] = [
            `firstname=${encodeURIComponent(firstname)}`,
            `surname=${encodeURIComponent(surname)}`,
            `email=${encodeURIComponent(email)}`,
            `order_number=${encodeURIComponent(orderNumber)}`,
            `order_subtotal=${encodeURIComponent(orderSubtotal)}`,
            `order_currency=${encodeURIComponent(orderCurrency)}`,
            `situation=${encodeURIComponent(situation)}`,
            `locale=${encodeURIComponent(locale)}`
        ];

        if (couponCode) {
            params.push(`coupon_code=${encodeURIComponent(couponCode)}`);
        }

        const src = config.mentionMeReferrer + `?${params.join('&')}`;

        console.log('Mention Me Referrer API URL:', src);

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.id = 'mention-me-referrer';
        document.body.appendChild(script);

        return () => {
            script.remove();
        };
    }, [order, config]);
    
    return (
        <>              
  
            <section className="typ-page">    
                <div className="typ-page-container">
                {!!order && !!order.orderItem && order.orderItem.length > 0 && !imagepushed? <img height={'1'} width={'1'} border={'0'} style={{display: 'none'}} onload={() => setimagepushed(true)} src={config.wunderkind.imageURL.replace('{{0}}', order.orderId)
                 .replace('{{1}}', order.orderItem[0].email1.toLowerCase())
                 .replace('{{2}}', convertNumber(order.grandTotal))
                 .replace('{{3}}', order.grandTotalCurrency)}/> : <SimpleElementSkeleton width={'0px'} height={'0px'} />}                                   
                    <div className="typ-page-left">
                        {<TrustPilotWidget />}
                        <PageTitle label={ getLabelFallback('TYP_PAGE_TITLE') } /> 
                        <div className="typ-page-left-content">
                            <span>{getLabelFallback('TYP_ORDER_CONFIRMATION_LABEL')} {!!order && !!order.orderItem && order.orderItem.length > 0 ? <span className="typ-page-left-content-email">{order.orderItem[0].email1}</span> : <SimpleElementSkeleton width={'150px'} height={'10px'} />}</span>
                            {!buyerName &&
                                <span dangerouslySetInnerHTML={{__html: getLabelFallback('TYP_SIGNIN_LABEL')
                                    .replace('{SIGN_IN}', '<button id="login-typ" class="primary-button">'+ getLabelFallback('TYP_SIGNIN_LINK_LABEL')+'</button>')
                                    .replace('{CREATE_ACCOUNT}', '<button id="create-account-typ" class="primary-button">'+ getLabelFallback('TYP_CREATE_ACCOUNT_LABEL')+'</button>')}}>
                                </span>
                            }
                            
                            <div className="typ-page-left-content-order">
                                {!!order ?
                                    <div className="typ-page-left-content-order-details">
                                        <span><strong>{ getLabelFallback('TYP_ORDER_NUMBER_LABEL') }: </strong>{order.orderId}</span>
                                        <span><strong>{ getLabelFallback('TYP_ORDER_DATE_LABEL') }: </strong>{getDateInAmericanFormat(order.lastUpdateDate)}</span>
                                    </div> :
                                    <div style={{margin: '16px 0'}}><SimpleElementSkeleton width={'100%'} height={'76px'} /></div>
                                }
                            </div>

                            <div className="typ-page-left-resume">
                                {!!formattedOrderItems ?
                                    <>{ getCartProduct(formattedOrderItems) }</> :
                                    <SimpleElementSkeleton width={'100%'} height={'400px'} />
                                }  
                            </div>
                        </div>
                    </div>
                        
                    <div className="typ-page-right">
                        <div className="typ-page-right-container">
                            {!!order && !!order.orderItem && order.orderItem.length > 0 ?
                                <div className="typ-page-right-container-wrapper">
                                    <div className="typ-page-right-container-details">
                                        <div className="typ-page-right-container-details-title">
                                            { getLabelFallback('TYP_SHIPPING_LABEL') }
                                        </div>
                                        <div className="typ-page-right-container-details-content">
                                            <span>{ order.orderItem[0].shipModeDescription.toLowerCase() === 'speedy rx' ? getLabelFallback('SPEEDY_RX_DETAIL_TEXT') : order.orderItem[0].shipModeDescription}</span>
                                            <span>{ order.orderItem[0].shippingCharge === '0.00000' && order.orderItem[0].shipModeDescription.toLowerCase() != 'speedy rx' ? getLabelFallback('FREE_SHIPPING_LABEL') : getLabelFallback('CURRENCY_LABEL') +' '+convertNumber(order.totalShippingCharge)}</span>
                                        </div>

                                        {
                                            isBossSelected && !!storeBossSelected && !!storeBossSelected[0] &&
                                            <>
                                                <div className="typ-page-right-container-details-boss">
                                                    <span>{storeBossSelected[0].Description[0].displayStoreName.toLowerCase().replace('lenscrafters', 'LensCrafters')}</span>
                                                    <span>{storeBossSelected[0].addressLine[0].toLowerCase()}, {storeBossSelected[0].city.toLowerCase()}, {storeBossSelected[0].stateOrProvinceName}</span>
                                                </div>
                                                <div className="typ-page-right-container-details-boss-claim">
                                                    { getLabelFallback('TYP_BOSS_CLAIM_LABEL') }
                                                </div>
                                            </>
                                        }
                                    </div>
                                </div>:
                                <SimpleElementSkeleton width={'100%'} height={'92px'}/>
                            }
                        </div>
                    
                        
                        <div className="typ-page-right-container">
                            {!!order && !!order.orderItem && order.orderItem.length > 0 ?
                                <div className="typ-page-right-container-details">
                                    <div className="typ-page-right-container-details-title">
                                        {
                                            isBossSelected ?
                                            getLabelFallback('TYP_BOSS_BILLING_LABEL')
                                            : getLabelFallback('TYP_DELIVERY_LABEL') 
                                        }:
                                    </div>
                                    {
                                        isBossSelected ? 
                                        <div className="typ-page-right-container-details-content mod">
                                            <span>{order.orderItem[0].firstName} {order.orderItem[0].lastName}, {order.orderItem[0].email1}</span>
                                            <span>{order.orderItem[0].phone1}</span>
                                        </div>
                                        : <div className="typ-page-right-container-details-content mod">
                                            <span>{order.orderItem[0].firstName} {order.orderItem[0].lastName}, {order.orderItem[0].addressLine[0]}, {order.orderItem[0].city}, {order.orderItem[0].state} {order.orderItem[0].zipCode}</span>
                                            <span>{order.orderItem[0].phone1}</span>
                                        </div>
                                    }
                                    
                                </div> :
                                <SimpleElementSkeleton width={'100%'} height={'92px'}/>
                        }
                        </div>
                        
                        { !!order &&  <img src={`//d1f0tbk1v3e25u.cloudfront.net/pc/r12f1knh/?e=${order.orderItem[0].email1}&r=${order.orderId}`} id="__tms_pc" height="1" width="1" /> }
                        <div className="typ-page-right-container">
                            <div className="typ-page-right-container-details">
                                <div className="typ-page-right-container-details-title">
                                    { getLabelFallback('TYP_TAXES_LABEL') }
                                    <span>{!!order ? getTotalTax(order) : <SimpleElementSkeleton width={'100px'} height={'24px'} />}</span>
                                </div>
                            </div>
                        </div>   
                        
                        <div className="typ-page-right-container">
                            <div className="typ-page-right-container-details">
                                <div className="typ-page-right-container-details-title">
                                        { getLabelFallback('TYP_PAYMENT_LABEL') }:
                                </div>
                                <div className="typ-page-right-container-details-content">
                                        <div>
                                        {!!order && !!order.paymentInstruction ? getPaymentImage(order.paymentInstruction[0].payMethodId.toLowerCase()) : getPaymentImage('glapplepay')}
                                            { !!order && !!order.paymentInstruction &&
                                                (order.paymentInstruction[0].payMethodId.toLowerCase() === 'visa' || 
                                                order.paymentInstruction[0].payMethodId.toLowerCase() === 'mastercard' ||
                                                order.paymentInstruction[0].payMethodId.toLowerCase() === 'discover' ||
                                                order.paymentInstruction[0].payMethodId.toLowerCase() === 'amex') &&
                                                <div className="typ-page-right-container-content-text">
                                                    { 
                                                        Array.from(Array(4).keys()).map(el => {
                                                            return (
                                                                <div key={"div-hide-card-number" + el}>
                                                                    {
                                                                        Array.from(Array(4).keys()).map(item => {
                                                                            return(
                                                                                <span key={'hide-card-number' + el + item}>&#9679;</span>
                                                                            )
                                                                        }) 
                                                                    }
                                                                </div>
                                                            )
                                                        })                  
                                                    }
                                                </div>
                                            }
                                            {!!order && !!order.paymentInstruction && order.paymentInstruction[0].payMethodId.toLowerCase() === 'glklarna' &&
                                                <div className="typ-page-right-container-content-text">
                                                    { getLabelFallback('KLARNA_TYP_LABEL') }
                                                </div>
                                            }                                         
                                        </div>
                                </div>
                            </div>
                        </div>
						
                        <div className="typ-page-right-container">
                            <div className="typ-page-right-container-details">
                                <div className="typ-page-right-container-details-title uppercase">
                                    { getLabelFallback('TYP_TOTAL_LABEL') }

                                    {!!order && <span>{getLabelFallback('CURRENCY_LABEL')}{convertNumber(order.grandTotal)}</span>}
                                </div>
                            </div>
                        </div>
                        <div className="typ-page-right-button">
                            <a href={config.linkContinueShopping} aria-label={getLabelFallback('TYP_CONTINUE_SHOPPING_LABEL')} className="violet-button">{getLabelFallback('TYP_CONTINUE_SHOPPING_LABEL')}</a>
                        </div>
                    </div>
                </div>
            </section>
            
            {isLoading &&
                <CartLoader />
            }
            {
                showSessionModal && <SessionModal />
            }
            {
                showGenericErrorModal && <GenericErrorModal />
            }
            {espotTyp && <span dangerouslySetInnerHTML={{__html: espotTyp}}></span>}
        </>
    );
}

export default TypPage;
